import { trainingAPI } from "@api/TrainingAPI";
import { Card, Switch, Typography, Row, Select, TimePicker, Input, Space, Tooltip } from "antd"
import { useMemo, useState } from "react"
import { Flags, flagTypesOptions, HOURS_IN_DAY, HOURS_IN_SIX_MONTHS, HOURS_IN_WEEK, IFlagReminderData, IFocusedMetrics } from "./constants";
import { UserDefinedFlag } from "@store/reducers/userDefinedFlags.reducer";
import dayjs, { Dayjs } from 'dayjs';
import { useSelector } from "react-redux";
import { audioMessage } from "@components/Common/Notifications/audioMessage";
import "@styles/styles-Settings.css"
import { PermissionedButton } from "@components/PermissionedComponents";

const { Text } = Typography;
const { Option } = Select;

const format = 'HH:mm';
type TimeFrameType = "hour" | "day" | "week"

export const FlagReminderCard = ({ title, type, data, clearNewReminder, focusedMetrics }: IFlagReminderCard) => {
    const determinTimeFrame = (): TimeFrameType => {
        if (data.lookback_hours > HOURS_IN_WEEK && Number.isInteger(data.lookback_hours / HOURS_IN_WEEK)) {
            return 'week'
        } else if (data.lookback_hours > HOURS_IN_DAY && Number.isInteger(data.lookback_hours / HOURS_IN_DAY)) {
            return 'day'
        }

        return 'hour'
    }

    const determineDuration = (): number => {
        if (data.lookback_hours > HOURS_IN_WEEK && Number.isInteger(data.lookback_hours / HOURS_IN_WEEK)) {
            return data.lookback_hours / HOURS_IN_WEEK
        } else if (data.lookback_hours > HOURS_IN_DAY && Number.isInteger(data.lookback_hours / HOURS_IN_DAY)) {
            return data.lookback_hours / HOURS_IN_DAY
        }

        return data.lookback_hours
    }

    const getDurationInHours = () => {
        let durationInHours = duration

        if (durationTimeFrame === 'week') {
            durationInHours = duration * HOURS_IN_WEEK
        } else if (durationTimeFrame === 'day') {
            durationInHours = duration * HOURS_IN_DAY
        }

        return durationInHours
    }

    const [activeToggleState, setActiveToggleState] = useState<boolean>(data.is_active)
    const [flagTypeOptions, setFlagTypeOptions] = useState<Flags[]>(data.metadata.flag_types)
    const [reminderTime, setReminderTime] = useState<string>(data.reminder_time)
    const [coachEmails, setCoachEmails] = useState<string[]>(data.metadata.recipients)
    const [durationTimeFrame, setDurationTimeFrame] = useState<TimeFrameType>(determinTimeFrame())
    const [duration, setDuration] = useState<number>(determineDuration())
    const [metrics, setMetrics] = useState<string[]>(data.metadata.individual_metrics || [])
    const [userDefinedFlagIds, setUserDefinedFlagIds] = useState<number[]>(data.metadata.user_defined_flag_ids || [])

    const handleChange = (value: Flags[]) => {
        setFlagTypeOptions(value)
        if (!value.includes(Flags.UserDefined)) setUserDefinedFlagIds([])
    };

    const handleChangeTimePicker = (time: Dayjs | null, timeString: string) => {
        setReminderTime(timeString)
    }

    const handleDurationChange = (value: TimeFrameType) => {
        setDurationTimeFrame(value)
    }

    const handleMetricChange = (metric: string[]) => {
        setMetrics(metric)
    }

    const submit = async () => {
        const durationInHours = getDurationInHours()

        const flagReminderConfig = {
            "lookback_hours": durationInHours,
            "reminder_time": reminderTime,
            "metadata": {
                "recipients": coachEmails,
                "flag_types": flagTypeOptions,
                "individual_metrics": metrics.includes(allMetricsValue) ? [""] : metrics,
                "user_defined_flag_ids": userDefinedFlagIds
            }
        }
        try {
            if (type === "new") {
                await trainingAPI.postFlagReminderConfig(flagReminderConfig)
                clearNewReminder()
            } else {
                await trainingAPI.patchFlagReminderConfig({
                    id: data.id,
                    "is_active": activeToggleState,
                    ...flagReminderConfig
                })
            }
            audioMessage.success("Saved!");
        } catch (err) {
            const errorResponseData = Object.entries(err.response.data)
            audioMessage.error(`Save Failed! ${errorResponseData[0][0]} - ${errorResponseData[0][1]}`);
        }

    }

    const {
        info: {
            coach_list: coachList
        }
    } = useSelector(({ team }) => team);

    const userDefinedFlags: UserDefinedFlag[] = useSelector(({ userDefinedFlags }: any) => userDefinedFlags.flags);

    const userDefinedFlagOptions = useMemo(
        () => userDefinedFlags.filter((flag) => flag.is_active).map((flag) => ({
            label: flag.name,
            value: flag.id as number
        })),
        [userDefinedFlags]
    )

    const availableFlagTypeOptions = useMemo(
        () => flagTypesOptions.map((e) => ({
            ...e,
            disabled: e.value === Flags.UserDefined && !userDefinedFlagOptions.length
        })),
        [userDefinedFlagOptions]
    )

    const allMetricsValue = ""

    const metricsOptions = useMemo(() => {
        const options: {
            label: string,
            options: { label: string, value: string }[]
        }[] = []

        if (flagTypeOptions.includes(Flags.Forceplate)) {
            options.push({
                label: 'Forceplate',
                options: focusedMetrics.filter((e) => e.metric_type === Flags.Forceplate).map((e) => {
                    return {
                        label: e.name,
                        value: `forceplate-${String(e.metric_id)}`
                    }
                })
            })
        }

        if (flagTypeOptions.includes(Flags.Testing)) {
            options.push({
                label: 'Testing',
                options: focusedMetrics.filter((e) => e.metric_type === Flags.Testing).map((e) => {
                    return {
                        label: e.name,
                        value: `testing-${String(e.metric_id)}`
                    }
                })
            })
        }

        return options;
    }, [flagTypeOptions, focusedMetrics])

    return (
        <>
            <Card title={title} extra={
                <>
                    <Switch
                        defaultChecked
                        checkedChildren={<Text style={{ color: 'white' }}>Active</Text>}
                        unCheckedChildren={<Text>Inactive</Text>}
                        checked={activeToggleState}
                        onChange={() => setActiveToggleState(!activeToggleState)}
                    />
                </>
            }>
                <div className="team-settings-input-wrap settings-first-field settings-inline-field">
                    <div className="team-settings-input-label">Flag types</div>
                    <Select
                        mode="multiple"
                        allowClear
                        value={flagTypeOptions}
                        placeholder="Select flag types"
                        onChange={handleChange}
                        options={availableFlagTypeOptions}
                        optionRender={(option: any) =>
                            option.data.disabled ? (
                                <Tooltip title="No user defined flags found for the team">
                                    <div>{option.data.label}</div>
                                </Tooltip>
                            ) : (
                                option.data.label
                            )
                        }
                        style={{ width: '100%' }}
                        maxTagCount={'responsive'}
                    />
                </div>

                {(flagTypeOptions.includes(Flags.Testing) || flagTypeOptions.includes(Flags.Forceplate)) &&
                    <div className="team-settings-input-wrap settings-inline-field">
                        <div className="team-settings-input-label">Metrics</div>
                        <div>
                            <Select
                                mode="multiple"
                                allowClear
                                value={metrics}
                                placeholder="Select metrics"
                                onChange={handleMetricChange}
                                options={metricsOptions}
                                style={{ width: '100%' }}
                                maxTagCount={'responsive'}
                                filterOption={(input, option) => {
                                    return option?.label?.toLowerCase()?.includes(input.toLowerCase()) || false
                                }}
                            />
                            {metrics.includes(allMetricsValue) &&
                                <div><Text type="warning">Not recommended picking all metrics</Text></div>
                            }
                        </div>
                    </div>
                }

                {flagTypeOptions.includes(Flags.UserDefined) &&
                    <div className="team-settings-input-wrap settings-inline-field">
                        <div className="team-settings-input-label">User defined flags</div>
                        <Select
                            mode="multiple"
                            allowClear
                            value={userDefinedFlagIds}
                            placeholder="Select user defined flags"
                            onChange={setUserDefinedFlagIds}
                            options={userDefinedFlagOptions}
                            style={{ width: '100%' }}
                            maxTagCount={'responsive'}
                            filterOption={(input, option) => {
                                return option?.label?.toLowerCase()?.includes(input.toLowerCase()) || false
                            }}
                        />
                    </div>
                }

                <div className="team-settings-input-wrap settings-inline-field">
                    <div className="team-settings-input-label">Remind me in</div>
                    <div>
                        <Space wrap>
                            <Input
                                defaultValue="10"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value as unknown as number)}
                                type="number"
                                max={999}
                                min={1}
                                style={{ width: 80 }}
                            />
                            <Select defaultValue="hour" value={durationTimeFrame} onChange={(e) => handleDurationChange(e as any)} style={{ width: 120 }}>
                                <Option value="hour">Hour(s)</Option>
                                <Option value="day">Day(s)</Option>
                                <Option value="week">Week(s)</Option>
                            </Select>
                            <Text type="secondary">(Max 6 months)</Text>
                        </Space>
                        {getDurationInHours() > HOURS_IN_SIX_MONTHS &&
                            <div><Text type="danger">Cannot be greater than 6 months</Text></div>
                        }
                    </div>
                </div>

                <div className="team-settings-input-wrap settings-inline-field">
                    <div className="team-settings-input-label">Remind me at</div>
                    <div>
                        <TimePicker
                            value={dayjs(reminderTime, "HH:mm")}
                            defaultValue={dayjs('12:00', format)}
                            onChange={handleChangeTimePicker}
                            format={format}
                            minuteStep={15}
                        />
                    </div>
                </div>

                <div className="team-settings-input-wrap settings-inline-field">
                    <div className="team-settings-input-label">Recipients</div>
                    <Select
                        mode="multiple"
                        allowClear
                        value={coachEmails}
                        placeholder="Select coaches"
                        onChange={(e) => setCoachEmails(e)}
                        options={coachList.map((e: any) => { return {
                            label: (<>{e?.first_name} {e?.last_name} {e.email}</>), 
                            value: e.email 
                        }})}
                        style={{ width: '100%' }}
                    />
                </div>

                <Row justify={"end"}>
                    <PermissionedButton permissionCheck="EditFlagReminder" type="primary" onClick={submit} disabled={getDurationInHours() > HOURS_IN_SIX_MONTHS}>
                        Save Reminder
                    </PermissionedButton>
                </Row>
            </Card >
        </>
    )
}

interface IFlagReminderCard {
    title: string;
    type: "new" | "existing",
    data: IFlagReminderData,
    clearNewReminder: () => void,
    focusedMetrics: IFocusedMetrics[]
}

export default FlagReminderCard